/** Form building blocks: Field (label + control + hint/error), Select, Combobox, Callout, ConfirmDialog. */
import { Command as CommandPrimitive } from 'cmdk';
import { AlertTriangle, Calendar, Check, ChevronsUpDown, Info, X } from 'lucide-react';
import { Popover as PopoverPrimitive, Select as SelectPrimitive } from 'radix-ui';
import { useId, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { formatDate, parseDate } from '../lib/dates.js';
import { Button } from './button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './overlays.js';
import { Input, Label } from './primitives.js';

const triggerBase =
  'flex h-8 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-left text-sm whitespace-nowrap shadow-xs [&>span]:truncate outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive data-[placeholder]:text-muted-foreground';

const popoverSurface =
  'z-50 rounded-lg border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95';

/**
 * Label + control + hint or error, wired for accessibility. The child receives `id` and `aria-*`
 * through the render function.
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  children: (props: {
    id: string;
    'aria-invalid'?: true;
    'aria-describedby'?: string;
  }) => ReactNode;
}) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children({
        id,
        ...(error ? { 'aria-invalid': true as const } : {}),
        ...(message ? { 'aria-describedby': messageId } : {}),
      })}
      {message ? (
        <p
          id={messageId}
          className={cn('text-xs', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

export interface Option<V extends string = string> {
  value: V;
  label: string;
  /** Secondary text shown in the list (e.g. an e-mail or a path). */
  description?: string;
  disabled?: boolean;
}

/** Native-feeling select for short, fixed lists. */
export function Select<V extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  ...aria
}: {
  value: V | undefined;
  onValueChange: (value: V) => void;
  options: Option<V>[];
  placeholder?: string;
  className?: string;
  id?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  disabled?: boolean;
}) {
  return (
    <SelectPrimitive.Root
      value={value ?? ''}
      onValueChange={(v) => onValueChange(v as V)}
      disabled={aria.disabled}
    >
      <SelectPrimitive.Trigger className={cn(triggerBase, className)} {...aria}>
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon>
          <ChevronsUpDown className="size-3.5 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            popoverSurface,
            'max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-default items-center rounded-md py-1.5 pr-8 pl-2 text-sm outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-accent"
              >
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2">
                  <Check className="size-4" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/** Searchable select for long lists (employees, units). Search ignores Vietnamese diacritics. */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearable,
  className,
  defaultOpen = false,
  onOpenChange,
  ...aria
}: {
  value: string | null | undefined;
  onValueChange: (value: string | null) => void;
  options: Option[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearable?: boolean;
  className?: string;
  /** Opens the list right away (pickers shown on demand, e.g. @mentions). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  id?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const selected = options.find((o) => o.value === value);
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div className={cn('relative', className)}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className={triggerBase}
            {...aria}
          >
            <span className={cn('truncate', !selected && 'text-muted-foreground')}>
              {/* Labels may be indented (tree pickers); the trigger shows them flush. */}
              {selected?.label.trim() ?? placeholder}
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverPrimitive.Trigger>
        {clearable && selected ? (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => onValueChange(null)}
            className="absolute top-1/2 right-7 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(popoverSurface, 'w-[var(--radix-popover-trigger-width)] min-w-64 p-0')}
        >
          <CommandPrimitive
            filter={(itemValue, search) => (fold(itemValue).includes(fold(search)) ? 1 : 0)}
          >
            <CommandPrimitive.Input
              placeholder={searchPlaceholder}
              className="h-9 w-full border-b bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
            />
            <CommandPrimitive.List className="max-h-64 overflow-y-auto p-1">
              <CommandPrimitive.Empty className="py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </CommandPrimitive.Empty>
              {options.map((o) => (
                <CommandPrimitive.Item
                  key={o.value}
                  value={`${o.label} ${o.description ?? ''} ${o.value}`}
                  disabled={o.disabled}
                  onSelect={() => {
                    onValueChange(o.value);
                    setOpen(false);
                  }}
                  className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent"
                >
                  <Check
                    className={cn(
                      'size-4 shrink-0',
                      o.value === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {o.description ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {o.description}
                      </span>
                    ) : null}
                  </span>
                </CommandPrimitive.Item>
              ))}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Lower-case and strip Vietnamese diacritics, for client-side search. */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase();
}

/** Inline notice: information, warning or error. */
export function Callout({
  variant = 'info',
  title,
  children,
  action,
  className,
}: {
  variant?: 'info' | 'warning' | 'error';
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const Icon = variant === 'info' ? Info : AlertTriangle;
  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex gap-3 rounded-lg border px-3 py-2.5 text-sm',
        variant === 'info' && 'border-info/25 bg-info/8',
        variant === 'warning' && 'border-warning/40 bg-warning/10',
        variant === 'error' && 'border-destructive/30 bg-destructive/8',
        className,
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 size-4 shrink-0',
          variant === 'info' && 'text-info',
          variant === 'warning' &&
            'text-[color-mix(in_oklch,var(--warning),black_30%)] dark:text-warning',
          variant === 'error' && 'text-destructive',
        )}
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className="text-muted-foreground">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}

/** Confirmation for consequential actions (suspend, delete…). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive,
  loading,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" hideClose>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A date field shown and typed as dd/mm/yyyy (the browser's own date input follows its locale),
 * with the native calendar behind the button. `value` and `onValueChange` use `YYYY-MM-DD`; an
 * emptied field gives ''.
 */
export function DateInput({
  value,
  onValueChange,
  min,
  max,
  className,
  disabled,
  onBlur,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'min' | 'max'> & {
  value: string | null | undefined;
  onValueChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
}) {
  const shown = formatDate(value, '');
  const [text, setText] = useState(shown);
  const [synced, setSynced] = useState(shown);
  if (shown !== synced) {
    setSynced(shown);
    setText(shown);
  }
  const picker = useRef<HTMLInputElement>(null);
  const within = (iso: string) => (!min || iso >= min) && (!max || iso <= max);

  return (
    <div className={cn('relative', className)}>
      <Input
        {...props}
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        autoComplete="off"
        disabled={disabled}
        className="pr-8 tabular-nums"
        value={text}
        onChange={(e) => {
          let next = e.target.value.replace(/[^\d/]/g, '');
          // Digits alone (typed, pasted or replacing a selection) get their slashes; a trailing
          // slash is added only while typing forward, so deleting one is not undone.
          const grew = next.length > text.length;
          if (/^\d+$/.test(next)) {
            const digits = next.slice(0, 8);
            next = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)]
              .filter(Boolean)
              .join('/');
            if (grew && (digits.length === 2 || digits.length === 4)) next += '/';
          } else if (grew && /^\d{1,2}\/\d{2}$/.test(next)) next += '/';
          next = next.slice(0, 10);
          setText(next);
          if (!next) return onValueChange('');
          const iso = parseDate(next);
          if (iso && within(iso)) onValueChange(iso);
        }}
        onBlur={(e) => {
          const iso = parseDate(text);
          if (!text || !iso || !within(iso)) setText(shown);
          else setText(formatDate(iso));
          onBlur?.(e);
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-hidden
        className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none"
        onClick={() => picker.current?.showPicker()}
      >
        <Calendar className="size-4" />
      </button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute right-0 bottom-0 size-0 opacity-0"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </div>
  );
}
